import React, { useEffect, useState } from 'react';
import { Trash2, Package, Calendar, AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

/**
 * Analytics → καρτέλα «Φύρα». Εμφανίζεται μόνο για καταστήματα με
 * stores.waste_enabled (το tab κρύβεται αλλιώς).
 *
 * Η αξία είναι σε ΤΙΜΕΣ ΠΩΛΗΣΗΣ και δεν συμμετέχει σε κανένα χρηματικό σύνολο —
 * το ποσοστό επί του τζίρου είναι δείκτης απώλειας, όχι λογιστικό μέγεθος.
 */

interface WasteAnalyticsProps {
  storeId: number;
  dateFrom: string;
  dateTo: string;
}

interface Row { qty: number; value: number }
interface ProductRow extends Row { product_name: string }
interface ReasonRow extends Row { reason: string }
interface DayRow extends Row { day: string }

interface WasteSection {
  summary: { entries: number; qty: number; value: number };
  byProduct: ProductRow[];
  byReason: ReasonRow[];
  byDay: DayRow[];
}

const REASON_LABELS: Record<string, string> = {
  expired: 'Ληγμένο',
  damaged: 'Κατεστραμμένο',
  misprep: 'Λάθος παρασκευή',
  other: 'Άλλο',
};

const formatEuro = (n: number) =>
  new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(n || 0);

const formatDate = (s: string) =>
  new Intl.DateTimeFormat('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .format(new Date(s));

const WasteAnalytics: React.FC<WasteAnalyticsProps> = ({ storeId, dateFrom, dateTo }) => {
  const [waste, setWaste] = useState<WasteSection | null>(null);
  const [gross, setGross] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Το accounting μπαίνει μαζί για να βγει το «% επί του τζίρου» χωρίς
        // δεύτερο round-trip.
        const r = await fetch(
          `${API_BASE_URL}/api/analytics/stores/${storeId}/report-data?from=${dateFrom}&to=${dateTo}&sections=waste,accounting&detail=full`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } }
        );
        if (r.ok && !cancelled) {
          const data = await r.json();
          setWaste(data.waste || null);
          setGross(Number(data.accounting?.gross) || 0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [storeId, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="bg-surface-1/50 border border-line rounded-2xl p-6 animate-pulse">
        <div className="h-4 w-40 bg-surface-2 rounded mb-4" />
        <div className="h-24 bg-surface-2 dark:bg-surface-2/50 rounded" />
      </div>
    );
  }

  const header = (
    <div className="flex items-center gap-2 mb-4">
      <Trash2 className="w-5 h-5 text-amber-700 dark:text-amber-400" />
      <h3 className="font-semibold text-t1">Φύρα</h3>
      <span className="text-xs text-t4 ml-2">
        {formatDate(dateFrom)} – {formatDate(dateTo)}
      </span>
    </div>
  );

  if (!waste || waste.summary.entries === 0) {
    return (
      <div className="bg-surface-1/50 border border-line rounded-2xl p-6">
        {header}
        <p className="text-t3 text-sm">
          Δεν καταχωρήθηκε φύρα στο διάστημα {formatDate(dateFrom)} – {formatDate(dateTo)}.
        </p>
      </div>
    );
  }

  const { summary, byProduct, byReason, byDay } = waste;
  const sharePct = gross > 0 ? (summary.value / gross) * 100 : null;
  const maxProduct = Math.max(1, ...byProduct.map(p => p.value));
  const days = [...byDay].sort((a, b) => b.day.localeCompare(a.day)).slice(0, 30);
  const maxDay = Math.max(1, ...days.map(d => d.value));

  return (
    <div className="bg-surface-1/50 border border-line rounded-2xl p-5">
      {header}

      {/* Σύνοψη */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-xl p-4">
          <p className="text-xs text-t3 mb-1">Καταχωρήσεις</p>
          <div className="text-2xl font-bold text-t1">{summary.entries}</div>
        </div>
        <div className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-xl p-4">
          <p className="text-xs text-t3 mb-1">Τεμάχια</p>
          <div className="text-2xl font-bold text-t1">{summary.qty}</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <p className="text-xs text-amber-700/90 dark:text-amber-400/90 mb-1">Αξία</p>
          <div className="text-2xl font-bold text-amber-800 dark:text-amber-300">{formatEuro(summary.value)}</div>
          <p className="text-[11px] text-t4 mt-0.5">σε τιμές πώλησης</p>
        </div>
        <div className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-xl p-4">
          <p className="text-xs text-t3 mb-1">Επί του τζίρου</p>
          <div className="text-2xl font-bold text-t1">
            {sharePct === null ? '—' : `${sharePct.toFixed(2)}%`}
          </div>
          <p className="text-[11px] text-t4 mt-0.5">
            {sharePct === null ? 'χωρίς πωλήσεις' : `τζίρος ${formatEuro(gross)}`}
          </p>
        </div>
      </div>

      {/* Ανά λόγο */}
      {byReason.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {byReason.map(r => (
            <div key={r.reason} className="bg-surface-0/40 border border-line rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-700/80 dark:text-amber-400/80 flex-shrink-0" />
                <span className="text-xs text-t2 truncate">{REASON_LABELS[r.reason] || r.reason}</span>
              </div>
              <div className="text-lg font-bold text-t1">{formatEuro(r.value)}</div>
              <p className="text-[11px] text-t4">{r.qty} τμχ</p>
            </div>
          ))}
        </div>
      )}

      {/* Ανά προϊόν */}
      {byProduct.length > 0 && (
        <div className="bg-surface-0/40 rounded-xl p-4 border border-line mb-4">
          <div className="flex items-center gap-2 mb-3 text-t2">
            <Package className="w-4 h-4" />
            <h4 className="text-sm font-semibold">Ανά προϊόν</h4>
          </div>
          <div className="space-y-2">
            {byProduct.slice(0, 20).map(p => (
              <div key={p.product_name}>
                <div className="flex items-center justify-between text-xs mb-1 gap-3">
                  <span className="text-t2 truncate">{p.product_name}</span>
                  <span className="text-t3 flex-shrink-0 tabular-nums">
                    {p.qty} τμχ · <span className="text-amber-800 dark:text-amber-300 font-semibold">{formatEuro(p.value)}</span>
                  </span>
                </div>
                <div className="h-2 bg-surface-2 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${(p.value / maxProduct) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          {byProduct.length > 20 && (
            <p className="text-[11px] text-t4 mt-3">
              …και άλλα {byProduct.length - 20} προϊόντα — πλήρης λίστα στο export (PDF/Excel).
            </p>
          )}
        </div>
      )}

      {/* Ημερήσια κατανομή */}
      {days.length > 0 && (
        <div className="bg-surface-0/40 rounded-xl p-4 border border-line">
          <div className="flex items-center gap-2 mb-3 text-t2">
            <Calendar className="w-4 h-4" />
            <h4 className="text-sm font-semibold">Ημερήσια κατανομή (τελευταίες 30 ημέρες)</h4>
          </div>
          <div className="space-y-2">
            {days.map(d => (
              <div key={d.day}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-t2">{formatDate(d.day)}</span>
                  <span className="text-t3 flex-shrink-0 tabular-nums">
                    {d.qty} τμχ · {formatEuro(d.value)}
                  </span>
                </div>
                <div className="h-2 bg-surface-2 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${(d.value / maxDay) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-t4 mt-4">
        Η αξία υπολογίζεται σε τιμές πώλησης και δεν συμμετέχει στα χρηματικά σύνολα των αναφορών.
      </p>
    </div>
  );
};

export default WasteAnalytics;
